import { Box, Menu, MenuItem, TableCell, TableRow, Divider } from '@mui/material'
import React, { memo, useCallback, useState, useRef, useEffect } from 'react'
import TableCellWrapper from './TableCellWrapper'
import { getColumnWidth } from '../../../../../../src/utility/columnWidth.js'

import {
  getCommentId,
  getConstraintErrorIdForColumn,
  getFieldIdFromKey,
  getLegacySubfieldId,
  getReviewId,
  getTableCommentId,
  getTableReviewId,
} from '../../../utils/utils'

import {
  useFieldValidationError,
  usePreviousAllReviewData,
  usePreviousProcessData,
  useProcessContext,
} from '../../../../../store'

import Checkbox from '@mui/material/Checkbox'

import MoreVertIcon from '@mui/icons-material/MoreVert'
import { ACTIVITY_TYPES, TABLE_ADD_ROW_TYPE } from '../../../../../utility/constants'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ControlPointDuplicateIcon from '@mui/icons-material/ControlPointDuplicate'
import RowWiseDataEntryModal from './RowWiseDataEntryModal.jsx'
import { ON_CHANGE_DATA_TYPE } from '../TableField.js'

const label = {
  inputProps: {
    'aria-label': 'Checkbox demo',
  },
}

const TableRowWrapper = ({
  index,
  row,
  allRowsWithHeaders = [],
  columns,
  style = {},
  columnWidth = '100%',
  currentRowData,
  handleRowDataChange = () => {},
  currentCommentDataRow,
  currentReviewDataRow,
  isSelected,
  isSelectable,
  isImported,
  allowRowDelete,
  allowAddRows,
  allowTableHeaders,
  isFieldDisabled = () => false,
  isHidden = () => false,
  handleRowSelect = () => {},
  handleRowDelete = () => {},
  syncWithParent = () => {},
  addNewRowInBetween = () => {},
  addHeader = () => {},
  optionsDisabled,
  freezeColumnId,
  hiddenColumnIds = [],
  allValues,
  requireFullRowEntry = false,
  tableField,
}) => {
  const [rowOptionsAnchorEl, setRowOptionsAnchorEl] = useState(null)

  const [isDataEntryModalOpen, setIsDataEntryModalOpen] = useState(false)
  const heading1ref = useRef(null)
  const [actionWidth, setActionWidth] = useState(0)
  const serialNoRef = useRef(null)
  const [serialNoWidth, setSerialNoWidth] = useState(0)
  const fieldValidationError = useFieldValidationError((state) => state.fieldValidationError)

  const processContext = useProcessContext((state) => state.processContext)
  const activityType = processContext?.activityTemplate?.activity_type
  const version = processContext?.processData?.version

  useEffect(() => {
    if (heading1ref.current) {
      setActionWidth(heading1ref.current.offsetWidth)
    }

    if (serialNoRef.current) {
      setSerialNoWidth(serialNoRef.current.offsetWidth)
    }
  }, [])

  const handleRowOptionsClick = (event) => {
    event.stopPropagation()

    setRowOptionsAnchorEl(event.currentTarget)
  }

  const handleRowOptionsClose = () => {
    setRowOptionsAnchorEl(null)
  }

  const handleDataEntryModalOpen = useCallback(() => {
    if (!requireFullRowEntry || !currentRowData) {
      return
    }

    setIsDataEntryModalOpen(true)
  }, [requireFullRowEntry, currentRowData])

  const handleDataEntryModalClose = useCallback(() => {
    setIsDataEntryModalOpen(false)
  }, [])

  const previousReviewProcessDataValues = usePreviousProcessData((state) =>
    state.previousProcessData.prevReviewProcessData
      ? state.previousProcessData.prevReviewProcessData
      : {},
  )

  const previousAllReviews = usePreviousAllReviewData((state) => state.previousAllReviews)

  const previousReviewDataOfCurrentRow = columns?.reduce((acc, column) => {
    if (Object.keys(previousReviewProcessDataValues).length > 0) {
      if (!column.is_read_only || !column.is_computed) {
        return {
          ...acc,

          [getReviewId(column.id)]:
            previousReviewProcessDataValues.review_data[getTableReviewId(column.id, row)],
        }
      }

      return {
        ...acc,
      }
    }

    return acc
  }, {})

  const previousReviewCommentsDataOfCurrentRow = columns?.reduce((acc, column) => {
    if (Object.keys(previousReviewProcessDataValues).length > 0) {
      if (!column.is_read_only || !column.is_computed) {
        return {
          ...acc,

          [getCommentId(column.id)]:
            previousReviewProcessDataValues.review_comments_data[getTableCommentId(column.id, row)],
        }
      }

      return {
        ...acc,
      }
    }

    return acc
  }, {})

  const previousAllReviewsOfCurrentRow = columns?.reduce((acc, column) => {
    if (Object.keys(previousAllReviews).length > 0) {
      if (!column.is_read_only || !column.is_computed) {
        return {
          ...acc,

          [getReviewId(column.id)]: previousAllReviews[getTableReviewId(column.id, row)],
        }
      }

      return {
        ...acc,
      }
    }

    return acc
  }, {})

  const onChangeHandler = useCallback(
    async (id, value, dataType) => {
      await handleRowDataChange(id, value, dataType)
    },
    [handleRowDataChange],
  )

  const isReviewSwitchHidden = (field) => {
    return (activityType === ACTIVITY_TYPES.DATA_ENTRY && version === 1) || field.is_read_only
  }

  const renderSerialNo = (serialIndex) => {
    return (
      <TableCell
        ref={serialNoRef}
        // onClick={requireFullRowEntry ? handleDataEntryModalOpen : undefined}
        sx={{
          minWidth: 60,
          position: 'sticky',
          backgroundColor: 'white',
          left: actionWidth,

          // cursor: requireFullRowEntry ? 'pointer' : 'default',
        }}
      >
        {serialIndex + 1}
      </TableCell>
    )
  }

  const renderActions = (currentRow) => {
    return (
      <TableCell
        ref={heading1ref}
        sx={{
          position: 'sticky',
          left: 0,
          backgroundColor: 'white',
          minWidth: isSelectable ? 84 : 46,
          top: style.top,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <MoreVertIcon onClick={handleRowOptionsClick} />

          <Menu
            anchorEl={rowOptionsAnchorEl}
            open={Boolean(rowOptionsAnchorEl)}
            onClose={handleRowOptionsClose}
          >
            <MenuItem
              sx={{
                textTransform: 'capitalize',
              }}
              style={{
                columnGap: '5px',
              }}
              disabled={!allowAddRows}
              onClick={() => {
                addNewRowInBetween(currentRow, TABLE_ADD_ROW_TYPE.ADD_ROW)

                handleRowOptionsClose()
              }}
            >
              <AddCircleOutlineIcon fontSize="small" />
              Add Row
            </MenuItem>

            <MenuItem
              style={{
                columnGap: '5px',
              }}
              disabled={!allowAddRows}
              onClick={() => {
                addNewRowInBetween(currentRow, TABLE_ADD_ROW_TYPE.COPY_ROW)

                handleRowOptionsClose()
              }}
            >
              <ControlPointDuplicateIcon fontSize="small" />
              Copy Row
            </MenuItem>

            <MenuItem
              style={{
                columnGap: '5px',
              }}
              disabled={optionsDisabled || !allowTableHeaders}
              onClick={() => {
                addHeader(currentRow, allRowsWithHeaders)

                handleRowOptionsClose()
              }}
            >
              <TextFieldsIcon fontSize="small" />
              Add Header
            </MenuItem>

            <Divider
              style={{
                marginBlock: '4px',
              }}
            />

            <MenuItem
              sx={{
                color: 'red',
                columnGap: '5px',
              }}
              disabled={
                !(allRowsWithHeaders.filter((it) => it.row != null).length > 1) || !allowRowDelete
              }
              onClick={() => {
                if (
                  (activityType === ACTIVITY_TYPES.DATA_ENTRY ||
                    activityType === ACTIVITY_TYPES.LONG_RUNNING) &&
                  allowRowDelete
                ) {
                  handleRowDelete(currentRow)

                  handleRowOptionsClose()
                }
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
              Delete
            </MenuItem>
          </Menu>

          {isSelectable ? (
            <>
              <Checkbox
                {...label}
                checked={isSelected}
                color="primary"
                size="small"
                disabled={activityType !== ACTIVITY_TYPES.DATA_ENTRY}
                onClick={() => handleRowSelect(currentRow, index, currentRowData)}
              />
            </>
          ) : null}
        </Box>
      </TableCell>
    )
  }

  const calculateOffset = (columnIndex) => {
    const staticPartWidth = actionWidth + serialNoWidth

    const visibleColumns = columns.filter((field) => !hiddenColumnIds?.includes(field.id))

    if (columnIndex <= 0) {
      return staticPartWidth
    }

    return (
      staticPartWidth +
      visibleColumns
        .slice(0, columnIndex)
        .reduce(
          (accumulator, current) => accumulator + getColumnWidth(current, isReviewSwitchHidden),
          0,
        )
    )
  }

  const onModalDataSubmit = (data) => {
    Object.keys(data).forEach((key) => {
      if (!['updatedAt'].includes(key)) {
        const id = getFieldIdFromKey(key)?.id
        onChangeHandler(id, data[key], ON_CHANGE_DATA_TYPE.VALUE_DATA)
      }
    })
  }

  return (
    <>
      <TableRow id={`tr-${row}`} style={style}>
        {currentRowData &&
          (activityType === ACTIVITY_TYPES.DATA_ENTRY ||
            activityType === ACTIVITY_TYPES.LONG_RUNNING) &&
          renderActions(row)}

        {currentRowData && renderSerialNo(index)}

        {currentRowData &&
          columns
            ?.filter((field) => !hiddenColumnIds?.includes(field.id))
            ?.map((field) => {
              const visibleColumns = columns.filter(
                (column) => !hiddenColumnIds?.includes(column.id),
              )

              const freezeIndex = visibleColumns.findIndex((column) =>
                freezeColumnId?.includes(column.id),
              )

              const isFrozen = visibleColumns.indexOf(field) <= freezeIndex

              const visibleIndex = visibleColumns.indexOf(field)

              const leftOffset = calculateOffset(visibleIndex)

              if (isHidden(field, row)) {
                return null
              }

              return (
                <TableCellWrapper
                  key={getLegacySubfieldId(field.id, row)}
                  field={field}
                  rowId={row}
                  isSelectable={isSelectable}
                  isSelected={isSelected}
                  isImported={isImported}
                  rowData={currentRowData}
                  rowReview={currentReviewDataRow}
                  rowComment={currentCommentDataRow}
                  previousReviewDataOfCurrentRow={previousReviewDataOfCurrentRow}
                  previousReviewCommentsDataOfCurrentRow={previousReviewCommentsDataOfCurrentRow}
                  previousAllReviewsOfCurrentRow={previousAllReviewsOfCurrentRow}
                  onChangeValue={onChangeHandler}
                  columnWidth={columnWidth}
                  helperText={fieldValidationError[getConstraintErrorIdForColumn(field.id, row)]}
                  isFieldDisabled={isFieldDisabled}
                  syncWithParent={syncWithParent}
                  allValues={allValues}
                  isReviewSwitchHidden={isReviewSwitchHidden}
                  isFrozen={isFrozen}
                  leftOffset={leftOffset}
                  isDataEntryModal={false}
                  openDataEntryModal={() => setIsDataEntryModalOpen(true)}
                  requireFullRowEntry={requireFullRowEntry}
                />
              )
            })}
      </TableRow>

      {/* =====================================================================
          DATA ENTRY MODAL
      ===================================================================== */}

      {requireFullRowEntry && (
        <RowWiseDataEntryModal
          open={isDataEntryModalOpen}
          onClose={handleDataEntryModalClose}
          fullWidth
          maxWidth="sm"
          row={row}
          allValues={allValues}
          currentCommentDataRow={currentCommentDataRow}
          currentReviewDataRow={currentReviewDataRow}
          currentRowData={currentRowData}
          index={index}
          isImported={isImported}
          columns={columns}
          hiddenColumnIds={hiddenColumnIds}
          isFieldDisabled={isFieldDisabled}
          isHidden={isHidden}
          onSubmit={onModalDataSubmit}
          syncWithParent={syncWithParent}
          title={tableField.field_template_name}
        />
      )}
    </>
  )
}

export default memo(TableRowWrapper, (prevProps, nextProps) => {
  return (
    prevProps?.currentRowData?.updatedAt === nextProps?.currentRowData?.updatedAt &&
    prevProps?.currentReviewDataRow?.updatedAt === nextProps?.currentReviewDataRow?.updatedAt &&
    prevProps?.currentCommentDataRow?.updatedAt === nextProps?.currentCommentDataRow?.updatedAt &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.freezeColumnId === nextProps.freezeColumnId &&
    prevProps.requireFullRowEntry === nextProps.requireFullRowEntry
  )
})
